<template>
  <div class="confirmation">
    <div
      class="confirmation-scroll"
      :style="[{ height: scrollHeight + 'px' }]"
    >
      <div class="confirmation-header">
        <picture
          v-lazyload="{
            src: selectedHotel.propertyImage,
            set: [
              { width: '414', height: '180', breakpoint: 'xs' },
              { width: '810', height: '476', breakpoint: 'lg' },
            ],
            fit: 'crop',
            align: 'center',
          }"
          class="image-wrapper background"
        >
          <img
            class="image"
            src="data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw=="
            :alt="
              selectedHotel.propertyImageAlt || selectedHotel.propertyName
            "
          >
        </picture>
        <header-close :deactivate="deactivate" />
        <div class="confirmation-header-info">
          <div class="confirmation-header-heading">
            <div class="title">
              {{ tc("booking.yourStay") }}
            </div>
          </div>
          <div class="confirmation-header-property">
            <div
              data-component="confirmation-header-property-name"
              class="confirmation-header-property-name"
            >
              {{ selectedHotel.propertyName }}
            </div>
            <div class="confirmation-header-property-country">
              {{ selectedHotel.country }}
            </div>
            <div
              v-if="propertyHasChat || propertyHasPhone"
              class="confirmation-header-property-info"
            >
              <a
                v-if="propertyHasPhone"
                :href="`tel:${selectedHotel.phone}`"
                class="phone-icon"
              />
              <a
                v-if="propertyHasChat"
                :href="selectedHotel.liveChat"
                target="_blank"
                class="chat-icon"
              />
            </div>
            <a
              v-if="showEditStayCta"
              href="#"
              class="edit"
              :aria-label="tc('booking.edit') + ' ' + tc('booking.yourStay')"
              @click.prevent="$emit('change-page', 'hotel-list')"
            >
              {{ tc("booking.edit") }}
            </a>
          </div>
          <!-- This is a placeholder for the future header utility section - Ticket Number ST-1212 -->
          <div
            v-if="false"
            class="confirmation-header-utility"
          >
            <div class="confirmation-header-utility-left">
              <div class="icon info" />
              <div class="icon phone" />
              <div class="icon chat" />
            </div>
            <div class="icon notification" />
          </div>
        </div>
      </div>
      <div class="confirmation-alert">
        <alert
          v-if="showAlert"
          :show-alert="selectedHotel.alertEnabled"
          :has-close-icon="selectedHotel.alertHasCloseIcon"
          :text-color="selectedHotel.alertTextColor"
          :background-color="selectedHotel.alertBackgroundColor"
          :alert-label="selectedHotel.alertLabel"
          :alert-label-button="selectedHotel.alertLabelButton"
          @open-hotel-alerts="openHotelAlerts"
        />
      </div>
      <div class="confirmation-content">
        <div
          class="confirmation-booking-dates"
          data-component="confirmation-booking-dates"
        >
          <div class="section-title">
            <div class="label">
              {{ tc("booking.date") }}
            </div>
            <a
              href="#"
              class="edit button--text underline size-md black"
              :aria-label="tc('booking.edit') + ' ' + tc('booking.date')"
              @click.prevent="$emit('change-page', 'calendar')"
            >
              {{ tc("booking.edit") }}
            </a>
          </div>
          <div class="confirmation-booking-date">
            <div
              class="confirmation-booking-date-start"
              v-html="dateFilter(localQuery.stayDateStart)"
            />
            <div
              class="confirmation-booking-date-end"
              v-html="dateFilter(localQuery.stayDateEnd)"
            />
            <div class="confirmation-booking-date-nights">
              <span>{{ localRangeInDays }}</span>
              {{
                localRangeInDays > 1
                  ? tc("noums.nightPlural")
                  : tc("noums.nightSingular")
              }}
            </div>
          </div>
        </div>
        <div
          class="confirmation-room"
          data-component="confirmation-room"
        >
          <div class="section-title">
            <div class="label">
              {{ tc("booking.guestsAndRooms") }}
            </div>
            <a
              href="#"
              class="edit button--text underline size-md black"
              :aria-label="
                tc('booking.edit') + ' ' + tc('booking.guestsAndRooms')
              "
              @click.prevent="$emit('change-page', 'guests-and-rooms')"
            >
              {{ tc("booking.edit") }}
            </a>
          </div>
          <div class="confirmation-room-details">
            <span class="people">{{
              getSummaryDetails({ capitalized: true, customQuery: localQuery })
            }}</span>
          </div>
        </div>
        <div
          v-if="showSpecialCodeSection"
          class="confirmation-code"
          data-component="confirmation-code"
        >
          <div class="section-title">
            <div class="label">
              {{ tc("booking.specialCode") }}
            </div>
          </div>
          <button
            v-show="!specialCodesOpened && !havePromoValues"
            class="confirmation-code-add"
            @click="specialCodesOpened = true"
            @keydown.space.prevent="specialCodesOpened = true"
          >
            <div class="icon-plus" />
            <span
              class="code-text"
            >{{ tc("booking.add") }} {{ tc("booking.specialCode") }}</span>
          </button>
          <form
            v-show="specialCodesOpened || havePromoValues"
            class="mohg-form code-form"
            @submit.prevent=""
          >
            <div
              v-if="!isBookingEditMode"
              class="input"
            >
              <input
                id="iataNumber"
                v-model="iataNumber"
                tabindex="0"
                type="text"
                name="iataNumber"
                :class="[{ 'have-value': !!iataNumber }]"
              >
              <label
                for="iataNumber"
                class="floating"
              >
                {{ tc("formFields.iataNumber") }}
              </label>
            </div>
            <div class="input">
              <input
                id="promotionCode"
                v-model="promotionCode"
                tabindex="0"
                type="text"
                name="promotionCode"
                :class="[
                  {
                    'have-value': !!promotionCode,
                    'input-validation-error': employeePromosError,
                  },
                ]"
              >
              <label
                for="promotionCode"
                class="floating"
              >
                {{ tc("formFields.promoCode") }}
              </label>
              <span
                v-if="employeePromosError"
                data-valmsg-for="promotionCode"
                data-valmsg-replace="true"
                class="field-validation-error"
              >
                {{ tc("errors.promoCodeOnlyLoggedUser") }}
              </span>
            </div>
            <div class="input">
              <input
                id="groupCode"
                v-model="groupCode"
                tabindex="0"
                type="text"
                name="groupCode"
                :class="[{ 'have-value': !!groupCode }]"
              >
              <label
                for="groupCode"
                class="floating"
              >
                {{ tc("formFields.groupCode") }}
              </label>
            </div>
          </form>
        </div>
        <div
          v-if="showSignInSection"
          class="confirmation-signin"
          data-component="confirmation-signin"
        >
          <a
            href="#"
            @click.prevent="openAuth('login')"
            @keydown.space.prevent="openAuth('login')"
          >{{ tc("auth.signIn") }}</a>
          {{ tc("booking.or") }}
          <a
            href="#"
            @click.prevent="openAuth('registration')"
            @keydown.space.prevent="openAuth('registration')"
          >{{ tc("auth.join") }}</a>
          {{ tc("booking.signInDetails") }}
        </div>
      </div>
    </div>
    <div
      ref="footer"
      class="confirmation-footer"
      data-component="confirmation-footer"
    >
      <div class="confirmation-best-rate">
        <div class="title">
          {{ tc("booking.ourBestRate") }}
        </div>
        <v-dropdown
          class="tooltip tooltip-variant--booking"
          :distance="24"
          :skidding="120"
          popper-class="booking-confirmation"
        >
          <button
            class="info-black"
            role="presentation"
            :aria-label="tc('booking.bestPriceWarranty')"
          />

          <template #popper>
            <div class="tooltip-variant--booking-content">
              {{ tc("booking.bestPriceWarranty") }}
            </div>
          </template>
        </v-dropdown>
        <div class="close-btn">
          <span class="close" />
        </div>
      </div>
      <div class="confirmation-cta">
        <button
          :class="[{ loading: isLoading }]"
          @click="submit"
        >
          {{ tc("booking.roomsAndPackages") }}
          <div
            class="sr-only"
            role="status"
          >
            {{ isLoading ? tc('accessibility.loadingButtonStatus') : '' }}
          </div>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapGetters, mapState } from 'vuex'
  import {
    formatDateZone,
    getAlertsThatOverlapsStayDateRange,
  } from '@utils/date-helpers'
  import TabTrapMixin from '@mixins/tab-trap'
  import HeaderClose from '../header-close/header-close.vue'

  export default {
    name: 'Confirmation',
    components: {
      HeaderClose,
    },
    mixins: [TabTrapMixin],
    inject: ['updateLocalQuery', 'localQuery'],
    props: {
      checkAvailability: {
        type: Function,
        required: true,
      },
      deactivate: {
        type: Function,
        required: true,
      },
      currentHotel: {
        type: String,
        required: false,
      },
    },
    data: () => ({
      isLoading: false,
      specialCodesOpened: false,
      iataNumber: null,
      promotionCode: null,
      groupCode: null,
      scrollHeight: 'auto',
      isTravelAgent: false,
      employeePromosError: false,
      isReservationPage: false,
      isFanClub: false,
    }),
    computed: {
      ...mapState('global', ['isFanClubBooking']),
      ...mapGetters('i18n', ['tc']),
      ...mapGetters('booking', [
        'getDateRangeInDays',
        'getAdultDetails',
        'getRoomDetails',
        'getSummaryDetails',
        'getQuery',
        'getBookingTrackingV2',
        'bookingFlow'
      ]),
      ...mapGetters('hotelData', ['getHotelById']),
      ...mapState('responsive', { wHeight: 'height' }),
      ...mapState('auth', ['user']),
      ...mapState('modifyReservation', ['isBookingEditMode']),
      employeePromos() {
        return ['81MOCO81', '81STAY*2022', '22FMO6'].map((m) => m.toLocaleLowerCase())
      },
      selectedHotel() {
        return this.getHotelById(this.localQuery.hotelCode)
      },
      localRangeInDays() {
        return Math.round(this.getDateRangeInDays(this.localQuery))
      },
      havePromoValues() {
        return !!this.iataNumber || !!this.promotionCode || !!this.groupCode
      },
      onlyForSignedIn() {
        return (
          this.promotionCode
          && this.employeePromos.includes(
            this.promotionCode.trim().toLocaleLowerCase()
          )
          && !this.user
        )
      },
      showAlert() {
        return (
          this.selectedHotel.alertEnabled
          && getAlertsThatOverlapsStayDateRange(
            this.localQuery.stayDateStart,
            this.localQuery.stayDateEnd,
            this.selectedHotel.alerts
          ).length > 0 && !this.isBookingEditMode
        )
      },
      hasIATACode() {
        return !!this.iataNumber
      },
      showSpecialCodeSection() {
        if (this.isReservationPage) {
          return true
        }
        return !this.isFanClub
      },
      showSignInSection() {
        return !this.user && !this.isTravelAgent && !this.hasIATACode && !this.isFanClubBooking
      },
      showEditStayCta() {
        return (!this.isBookingEditMode && !this.currentHotel)
      },
      propertyHasChat() {
        return !!this.selectedHotel.liveChat
      },
      propertyHasPhone() {
        return !!this.selectedHotel.phone
      }
    },
    watch: {
      wHeight(val) {
        this.calculateScroll()
      },
      iataNumber(val) {
        this.updateLocalQuery({ prop: 'iataNumber', value: val })
        this.isTravelAgent = !!this.localQuery.iataNumber || this.isFanClubBooking
      },
    },
    mounted() {
      this.$emit('change-width', 'default')
      const queryParams = new URLSearchParams(window.location.search)
      const iataNumber = queryParams.get('iataNumber')
      const promotionCode = queryParams.get('promotionCode')
      const groupCode = queryParams.get('groupCode')
      this.isTravelAgent = !!(iataNumber || this.localQuery.iataNumber) || this.isFanClubBooking
      this.isReservationPage = window.location.pathname.includes('/Reservation')
        || window.location.pathname.includes('/reservation')
      this.isFanClub = !!(
        this.isTravelAgent && !!(promotionCode || this.localQuery.promotionCode)
      )
      this.iataNumber = this.localQuery.iataNumber ?? iataNumber
      this.promotionCode = this.localQuery.promotionCode ?? promotionCode
      this.groupCode = this.localQuery.groupCode ?? groupCode
      this.calculateScroll()
      this.$track(
        this.getBookingTrackingV2(this.localQuery, {
          event: 'step2'
        })
      )
      this.$track(
        this.getBookingTrackingV2(this.localQuery, {
          event: 'bookingStateChange',
          bookingEngine: {
            bookingStepNumber: '1.3',
            bookingStepName: 'Booking Details Flyout',
            bookingEngineVersion: this.isBookingEditMode ? '2' : '1',
            bookingFlow: this.bookingFlow(this.localQuery)
          },
        })
      )
    },
    methods: {
      async submit() {
        if (this.onlyForSignedIn) {
          this.$el.querySelector('[id="promotionCode"]').scrollIntoView()
          this.employeePromosError = true
          return
        }
        this.isLoading = true

        this.updateLocalQuery({
          prop: 'iataNumber',
          value: this.iataNumber
        })
        this.updateLocalQuery({
          prop: 'promotionCode',
          value: this.promotionCode
        })
        this.updateLocalQuery({
          prop: 'groupCode',
          value: this.groupCode
        })
        if (
          this.getQuery.hotelCode
          && this.getQuery.hotelCode !== this.localQuery.hotelCode
        ) {
          this.updateLocalQuery({ prop: 'roomCodes', value: [] })
          this.updateLocalQuery({ prop: 'bedType', value: null })
          this.updateLocalQuery({ prop: 'roomName', value: null })
        }
        await this.checkAvailability()
        this.isLoading = false
      },
      closeModal() {
        this.deactivate()
      },
      calculateScroll() {
        const footerHeight = this.$refs.footer?.getBoundingClientRect().height || 0
        this.scrollHeight = this.wHeight - footerHeight
      },
      openAuth(type = 'login') {
        this.$emit('change-login-component', type)
        this.$emit('change-width', 'small')
        this.$emit('change-page', 'auth-sidebar')
      },
      dateFilter(val) {
        const weekDate = formatDateZone(val, null, 'dddd[,]')
        const fullDate = formatDateZone(val, null, 'MMMM D[,] YYYY')
        return `${weekDate}<br>${fullDate}`
      },
      openHotelAlerts() {
        this.$emit('change-page', 'hotel-alerts')
      },
    },
  }
</script>
